import { CHUNK_FIRST_BYTE_TIMEOUT_MS, CHUNK_STALL_TIMEOUT_MS, putToS3WithStallTimeout } from "@uploads";

export { CHUNK_FIRST_BYTE_TIMEOUT_MS, CHUNK_STALL_TIMEOUT_MS, putToS3WithStallTimeout };

// Extracted upload logic as a pure function for reusability
// Uses async/await pattern for better readability and error handling
type BlobObject = {
	id: string;
	buffer: ArrayBuffer;
	conversationId: string;
	sessionClientId: string;
	chunkIndex: number;
};

export type UploadProgressEvent = {
	blobOrFile?: BlobObject | File | Blob;
	completed: boolean;
	loaded: number;
	total: number;
	percentage: number;
	durationSeconds: number;
};

/**
 * Uploads a blob to S3 using a presigned URL
 * @param {BlobObject | File} blobOrFile - The blob object or file object to upload
 * @param {string} presignedUrl - The presigned S3 URL for uploading
 * @returns Promise containing the fetch result, original blob/file, and upload start timestamp
 * @example
 * try {
 *   const { result } = await uploadToS3(blob, presignedUrl);
 *   console.log('Upload successful');
 * } catch (err) {
 *   console.error('Upload failed:', err.message);
 * }
 */
export async function uploadToS3(blobOrFile: BlobObject | File, presignedUrl: string, headers?: Readonly<Record<string, string>>) {
	if (!presignedUrl) {
		throw new TypeError("Presigned URL is required");
	}
	if (!blobOrFile) {
		throw new TypeError("blobOrFile is required");
	}

	const uploadStartTime = new Date().getTime();
	const body = blobOrFile instanceof File ? blobOrFile : new Blob([blobOrFile.buffer]);

	// LSP-16654: route the snapshot path through the canonical XHR transport so it gets the same
	// zero-progress stall protection as the chunked / single-PUT paths (the previous bare `fetch`
	// had no timeout and a truly stalled snapshot PUT could hang forever). Contract is unchanged:
	// resolve { result, blobOrFile, uploadStartTime } on 2xx, throw S3UploadError otherwise.
	let result: Response;
	try {
		result = await putToS3WithStallTimeout(body, presignedUrl, { headers });
	} catch (transportError) {
		// AbortError (stall) / NetworkError (transport failure) — normalize to the thrown
		// S3UploadError contract the snapshot caller (api/index.ts) already catches.
		throw toS3UploadError(transportError);
	}

	const uploadEndTime = new Date().getTime();

	if (result.ok) {
		console.log(`Uploaded blob/file to S3 in ${uploadEndTime - uploadStartTime}ms`);
		return { result, blobOrFile, uploadStartTime };
	} else {
		throw await parseS3XmlError(result);
	}
}

function extractS3XmlFields(xmlText: string): { code?: string; message?: string; requestId?: string } {
	const xml = new DOMParser().parseFromString(xmlText, "application/xml");
	const get = (tag: string) => xml.querySelector(tag)?.textContent ?? undefined;
	return { code: get("Code"), message: get("Message"), requestId: get("RequestId") };
}

/**
 * Maps a {@link putToS3WithStallTimeout} transport rejection (raw `AbortError` DOMException or
 * `TypeError`) onto the `S3UploadError` contract the callback/snapshot paths expect.
 */
function toS3UploadError(error: unknown): S3UploadError {
	if (error instanceof DOMException && error.name === "AbortError") {
		return new S3UploadError("Upload was aborted", { code: "AbortError" });
	}
	if (error instanceof S3UploadError) {
		return error;
	}
	return new S3UploadError(error instanceof Error ? error.message : "Network error occurred during upload", { code: "NetworkError" });
}

/**
 * Uploads a blob to S3 using a presigned URL with XHR for progress tracking
 * @param {BlobObject | File | Blob} blobOrFile - The blob object or file object to upload
 * @param {string} presignedUrl - The presigned S3 URL for uploading
 * @param {Function} callback - Callback function to handle upload result
 * @param {any} callback.result - The response (if successful) or error object (if failed)
 * @param {BlobObject | File | Blob} callback.blobOrFile - The blob object or file object to upload
 * @param {number} callback.uploadStartTime - Timestamp when upload started
 * @param {Function} progressCallback - Callback function to report upload progress
 * @param {number} progressCallback.loaded - Bytes uploaded so far
 * @param {number} progressCallback.total - Total bytes to upload
 * @param {number} progressCallback.percentage - Upload progress percentage (0-100)
 * @returns {Promise<void>} Promise that resolves when upload completes
 * @example
 * uploadToS3WithProgress(blob, presignedUrl, (result, blobOrFile, uploadStartTime) => {
 *   if (result.ok) {
 *     console.log('Upload successful');
 *   } else {
 *     console.error('Upload failed:', result.message);
 *   }
 * }, (loaded, total, percentage) => {
 *   console.log(`Upload progress: ${percentage}%`);
 * });
 */
export async function uploadToS3WithProgress(
	blobOrFile: BlobObject | File | Blob,
	presignedUrl: string,
	progressCallback: (event: UploadProgressEvent) => void,
	callback?: (result: Response | S3UploadError, blobOrFile: BlobObject | File | Blob, uploadStartTime: number) => void,
	cancelSignal?: AbortSignal,
	headers?: Readonly<Record<string, string>>
) {
	if (!presignedUrl) {
		console.error("Presigned URL is required");
		return;
	}

	if (!blobOrFile) {
		console.error("blobOrFile is required");
		return;
	}

	const uploadStartTime = new Date().getTime();

	const body = blobOrFile instanceof File || blobOrFile instanceof Blob ? blobOrFile : new Blob([blobOrFile.buffer]);

	// Dual contract preserved (LSP-16654): when a `callback` is supplied, ALL outcomes (success and
	// failure) are reported via the callback and the Promise RESOLVES; when none is supplied, success
	// resolves and failure REJECTS with the S3UploadError. FileUploadDialog uses the no-callback
	// (reject) form, so its try/catch on S3UploadError("AbortError"/"NetworkError") is unchanged.
	// Returning the transport's own promise chain directly (no deferred wrapper) keeps the two
	// settle paths in one place: report the result via callback, and re-throw only the no-callback
	// failure so the chain rejects.
	return putToS3WithStallTimeout(body, presignedUrl, {
		cancelSignal,
		headers,
		progressCallback: ({ loaded, total, percentage }) => {
			const completed = percentage === 100;
			const durationSeconds = completed ? (new Date().getTime() - uploadStartTime) / 1000 : 0;
			progressCallback({ blobOrFile, completed, loaded, total, percentage: Math.min(percentage, 99), durationSeconds });
		}
	}).then(
		async (response) => {
			if (response.ok) {
				callback?.(response, blobOrFile, uploadStartTime);
				return;
			}
			const error = await parseS3XmlError(response);
			callback?.(error, blobOrFile, uploadStartTime);
			if (!callback) throw error;
		},
		(transportError) => {
			const error = toS3UploadError(transportError);
			callback?.(error, blobOrFile, uploadStartTime);
			if (!callback) throw error;
		}
	);
}

export interface S3ErrorOptions extends ErrorOptions {
	code?: string;
	requestId?: string;
	rawXml?: string;
	status?: number;
}

export class S3UploadError extends Error {
	readonly name = "S3UploadError";
	code?: string;
	requestId?: string | undefined;
	rawXml?: string | undefined;
	status?: number;

	constructor(message?: string, options: S3ErrorOptions = {}) {
		const { code, requestId, rawXml, status, ...errorOptions } = options;
		super(message, errorOptions);
		this.code = code;
		this.requestId = requestId;
		this.rawXml = rawXml;
		this.status = status;
	}
}

/**
 * Parses an S3 XML error response
 * @param {Response} response - The fetch response containing the XML error
 * @returns Error containing the error code, message, request ID, and raw XML (if available)
 */
export async function parseS3XmlError(response: Response) {
	const xmlText = await response.text();

	try {
		const { code, message, requestId } = extractS3XmlFields(xmlText);

		return new S3UploadError(message || "Failed to parse S3 XML error response", {
			code,
			requestId,
			status: response.status
		});
	} catch (err) {
		console.error("Failed to parse S3 XML error response", err);
		return new S3UploadError("Failed to parse S3 XML error response", {
			code: "XmlParseError",
			rawXml: xmlText,
			status: response.status,
			cause: err
		});
	}
}
